import { useEffect, useState, type ComponentType } from "react";
import {
  X,
  Edit,
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileText,
  Download,
  Clock,
  Eye,
  User,
  CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ImagePreview } from "@/components/ImagePreview";
import { getStatusLabel, StatusBadge, type Status } from "@/components/StatusBadge";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  DocumentViewer,
  resolveDocumentPreviewUrl,
} from "@/components/DocumentViewer";
import {
  formatFileName,
  getEducationQualificationLabel,
  getLifeStatus,
  getMembershipStatus,
} from "@/lib/utils";
import { formatDateDisplay } from "@/lib/dates";
import { useMemberContext } from "@/contexts/MemberContext";
import { usePaymentContext } from "@/contexts/PaymentContext";
import type { Member, ViewMemberProps } from "@/types";
import { sortPaymentsByDateDesc } from "@/lib/payments";
import { ViewPayment } from "@/pages/payments/ViewPayment";

const DRAWER_CLASS =
  "h-full !w-full !min-w-0 !max-w-full overflow-hidden flex flex-col sm:!w-[45vw] sm:!min-w-[650px] sm:!max-w-[45vw]";

const formatDate = (dateString: string) => formatDateDisplay(dateString);

const hasProofDocument = (name?: string | null, url?: string | null) =>
  Boolean(name?.trim() || url?.trim());

const formatPhone = (phone?: string) => {
  const digits = (phone ?? "").replace(/\D/g, "");
  if (!digits) return "--";
  if (digits.length === 10) return `+91 ${digits}`;
  return phone ?? "--";
};

const calcAgeFromDob = (dob?: string) => {
  if (!dob) return "--";
  const dobDate = new Date(dob);
  if (Number.isNaN(dobDate.getTime())) return "--";
  const today = new Date();
  let age = today.getFullYear() - dobDate.getFullYear();
  const m = today.getMonth() - dobDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
    age -= 1;
  }
  return age >= 0 ? String(age) : "--";
};

function InfoItem({
  icon: Icon,
  label,
  value,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex gap-3 rounded-lg border border-gray-100 bg-gray-50/80 p-3.5">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-xs ring-1 ring-gray-200/60">
        <Icon className="h-4 w-4 text-[#4369a0]" />
      </div>
      <div className="min-w-0">
        <p className="text-sm text-gray-700">
          {label}
        </p>
        <p className="mt-0.5 truncate text-sm font-medium text-gray-900">
          {value}
        </p>
      </div>
    </div>
  );
}

export function ViewMember({ member, open, onOpenChange, onEdit }: ViewMemberProps) {
  const { getMemberById } = useMemberContext();
  const { getPaymentsByMemberId, getManagementByMemberId } = usePaymentContext();
  const [memberDetails, setMemberDetails] = useState<Member | null>(null);
  const [viewingDocument, setViewingDocument] = useState<{
    name: string;
    url?: string;
    title: string;
  } | null>(null);
  const [isPaymentViewOpen, setIsPaymentViewOpen] = useState(false);

  const handleClose = () => onOpenChange(false);

  useEffect(() => {
    if (!open || !member?.id) {
      setMemberDetails(null);
      return;
    }

    setMemberDetails(getMemberById(member.id) ?? member);
  }, [open, member, getMemberById]);

  useEffect(() => {
    if (!open) {
      setViewingDocument(null);
      setIsPaymentViewOpen(false);
    }
  }, [open]);

  const memberPayments = memberDetails
    ? sortPaymentsByDateDesc(getPaymentsByMemberId(memberDetails.id))
    : [];
  const managementItem = memberDetails
    ? getManagementByMemberId(memberDetails.id)
    : undefined;
  const monthsPaid = managementItem?.paid_months ?? 0;
  const totalMonths = managementItem?.total_months ?? 100;
  const paymentSummary = {
    totalPaid: managementItem?.total_paid ?? 0,
    amountDue: managementItem?.due_this_today ?? 0,
    monthsPaid,
    monthsRemaining: Math.max(0, totalMonths - monthsPaid),
    paymentProgress: Math.min(totalMonths, monthsPaid),
  };
  const totalPaid = paymentSummary.totalPaid;
  const monthsRemaining = paymentSummary.monthsRemaining;
  const amountRemaining = paymentSummary.amountDue;
  const paymentProgress = paymentSummary.paymentProgress;
  const membershipStatus = memberDetails
    ? getMembershipStatus(memberDetails, paymentSummary.monthsPaid)
    : "active";

  const handleViewDocument = (
    docName: string,
    title: string,
    docUrl?: string,
  ) => {
    setViewingDocument({ name: docName, url: docUrl, title });
  };

  const handleDownloadDocument = (docName: string, docUrl?: string) => {
    const previewUrl = resolveDocumentPreviewUrl(docUrl);
    if (!previewUrl) return;

    const link = document.createElement("a");
    link.href = previewUrl;
    link.download = docName;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  const handleEdit = () => {
    if (!memberDetails || !onEdit) return;
    onEdit(memberDetails);
    handleClose();
  };

  const handleCloseDocument = () => setViewingDocument(null);

  const handleViewFullPaymentHistory = () => {
    if (!memberDetails) return;
    setIsPaymentViewOpen(true);
  };

  const handleDrawerOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && viewingDocument) return;
    onOpenChange(nextOpen);
  };

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={handleDrawerOpenChange}
        direction="right"
      >
        <DrawerContent className={DRAWER_CLASS}>
          <DrawerHeader className="shrink-0 border-b border-gray-200 bg-white px-5 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1">
                <DrawerTitle className="text-base font-semibold text-gray-900 ">
                  Member Details
                </DrawerTitle>
                {memberDetails && onEdit && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleEdit}
                        className="h-7 gap-1 px-2 text-xs font-normal text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                      >
                        <Edit className="h-3 w-3" />
                        <span className="sr-only">Edit member</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Edit member</TooltipContent>
                  </Tooltip>
                )}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DrawerClose asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 rounded-md border border-gray-200 text-gray-500 hover:border-[#4369a0]/30 hover:bg-[#4369a0]/10 hover:text-[#4369a0]"
                    >
                      <X className="h-3.5 w-3.5" />
                      <span className="sr-only">Close</span>
                    </Button>
                  </DrawerClose>
                </TooltipTrigger>
                <TooltipContent side="bottom">Close</TooltipContent>
              </Tooltip>
            </div>
          </DrawerHeader>

          <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide bg-gray-50/80 px-6 py-5">
            {memberDetails && (
              <div className="space-y-5">
                <div className="rounded-xl border border-gray-200/80 bg-white px-5 py-5">
                  <div className="flex items-start gap-3.5">
                    <ImagePreview src={memberDetails.profileImage || memberDetails.photo} alt={memberDetails.name || "Member photo"}>
                    <Avatar className="h-11 w-11 shrink-0">
                      <AvatarImage
                        src={memberDetails.profileImage || memberDetails.photo}
                      />
                      <AvatarFallback className="bg-gray-100 text-xs font-semibold tracking-wide text-gray-600">
                        {memberDetails.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                    </ImagePreview>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                        <h2 className="truncate text-base font-semibold tracking-tight text-gray-900">
                          {memberDetails.name}
                        </h2>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <StatusBadge status={membershipStatus} />
                          <StatusBadge status={getLifeStatus(memberDetails)} />
                        </div>
                      </div>
                      <p className="mt-0.5 text-[13px] tabular-nums text-gray-500">
                        #{memberDetails.id}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-3 gap-4 border-t border-gray-100 pt-5">
                    <div>
                      <p className="text-[20px] font-semibold tabular-nums tracking-tight text-gray-900">
                        ₹{totalPaid}
                      </p>
                      <p className="mt-0.5 text-[12px] text-gray-500">Total paid</p>
                    </div>
                    <div>
                      <p className="text-[20px] font-semibold tabular-nums tracking-tight text-gray-900">
                        ₹{amountRemaining}
                      </p>
                      <p className="mt-0.5 text-[12px] text-gray-500">Remaining</p>
                    </div>
                    <div>
                      <p className="text-[20px] font-semibold tabular-nums tracking-tight text-gray-900">
                        {monthsRemaining}
                      </p>
                      <p className="mt-0.5 text-[12px] text-gray-500">Months left</p>
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="mb-1.5 flex items-center justify-between text-[12px]">
                      <span className="text-gray-500">Progress</span>
                      <span className="tabular-nums text-gray-600">
                        {paymentProgress} of 100 months
                      </span>
                    </div>
                    <div className="h-1 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-[#4369a0] transition-all"
                        style={{ width: `${paymentProgress}%` }}
                      />
                    </div>
                  </div>
                </div>

                <section className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xs">
                  <div className="border-b border-gray-100 px-5 py-4">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                      <User className="h-4 w-4 text-[#4369a0]" />
                      Member Information
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-2">
                    <InfoItem
                      icon={Phone}
                      label="Phone Number"
                      value={formatPhone(memberDetails.phone)}
                    />
                    <InfoItem
                      icon={Mail}
                      label="Email"
                      value={
                        memberDetails.email?.trim() ? memberDetails.email : "--"
                      }
                    />
                    <InfoItem
                      icon={User}
                      label="Father/Husband Name"
                      value={
                        memberDetails.fatherOrHusbandName?.trim()
                          ? memberDetails.fatherOrHusbandName
                          : "--"
                      }
                    />
                    <InfoItem
                      icon={User}
                      label="Gender"
                      value={memberDetails.gender || "--"}
                    />
                    <InfoItem
                      icon={User}
                      label="Life Status"
                      value={getStatusLabel(getLifeStatus(memberDetails))}
                    />
                    <InfoItem
                      icon={Calendar}
                      label="Date Of Birth"
                      value={
                        memberDetails.dob ? formatDate(memberDetails.dob) : "--"
                      }
                    />
                    <InfoItem
                      icon={Clock}
                      label="Age"
                      value={calcAgeFromDob(memberDetails.dob)}
                    />
                    <InfoItem
                      icon={User}
                      label="Education qualification"
                      value={
                        getEducationQualificationLabel(memberDetails.educationQualification) ?? "--"
                      }
                    />
                    <InfoItem
                      icon={User}
                      label="Occupation"
                      value={
                        memberDetails.occupation?.trim()
                          ? memberDetails.occupation
                          : "--"
                      }
                    />
                    <InfoItem
                      icon={Calendar}
                      label="Joined Date"
                      value={formatDate(memberDetails.joinedDate)}
                    />
                    <InfoItem
                      icon={Clock}
                      label="End Date"
                      value={formatDate(memberDetails.endDate)}
                    />
                    <div className="md:col-span-2">
                      <InfoItem
                        icon={MapPin}
                        label="Address"
                        value={
                          memberDetails.address?.trim()
                            ? memberDetails.address
                            : "--"
                        }
                      />
                    </div>
                  </div>
                </section>

                <section className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xs">
                  <div className="border-b border-gray-100 px-5 py-4">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                      <User className="h-4 w-4 text-[#4369a0]" />
                      Nominee
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-2">
                    <InfoItem
                      icon={User}
                      label="Nominee Name"
                      value={
                        memberDetails.nomineeName?.trim()
                          ? memberDetails.nomineeName
                          : "--"
                      }
                    />
                    <InfoItem
                      icon={User}
                      label="Nominee Relationship"
                      value={
                        memberDetails.nomineeRelationship?.trim()
                          ? memberDetails.nomineeRelationship
                          : "--"
                      }
                    />
                  </div>
                </section>

                <section className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xs">
                  <div className="border-b border-gray-100 px-5 py-4">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                      <FileText className="h-4 w-4 text-[#4369a0]" />
                      Uploaded Documents
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-2">
                    {[
                      {
                        label: "Proof Document 1",
                        name: memberDetails.proof1,
                        url: memberDetails.proof1Url,
                      },
                      {
                        label: "Proof Document 2",
                        name: memberDetails.proof2,
                        url: memberDetails.proof2Url,
                      },
                    ].map((doc) => (
                      <div
                        key={doc.label}
                        className="rounded-lg border border-gray-200/80 bg-gray-50/50 p-4"
                      >
                        <p className="text-sm text-gray-700">
                          {doc.label}
                        </p>
                        {hasProofDocument(doc.name, doc.url) ? (
                          <div className="mt-3">
                            <div className="mb-3 flex items-start gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-xs ring-1 ring-gray-200/60">
                                <FileText className="h-5 w-5 text-[#4369a0]" />
                              </div>
                              <p
                                className="text-sm font-medium text-gray-800"
                                title={doc.name}
                              >
                                {formatFileName(doc.name!)}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 border-gray-200 bg-white"
                                disabled={!doc.url?.trim()}
                                onClick={() =>
                                  handleViewDocument(
                                    doc.name!,
                                    doc.label,
                                    doc.url,
                                  )
                                }
                              >
                                <Eye className="mr-1.5 h-3.5 w-3.5" />
                                View
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 border-gray-200 bg-white"
                                disabled={!doc.url?.trim()}
                                onClick={() =>
                                  handleDownloadDocument(doc.name!, doc.url)
                                }
                              >
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3">
                            <p className="text-sm text-gray-400">
                              No document uploaded
                            </p>
                            <div className="mt-3 flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 border-gray-200 bg-white"
                                disabled
                              >
                                <Eye className="mr-1.5 h-3.5 w-3.5" />
                                View
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 border-gray-200 bg-white"
                                disabled
                              >
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                <section className="overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-xs">
                  <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                      <CreditCard className="h-4 w-4 text-[#4369a0]" />
                      Recent Payments
                    </h3>
                    <button
                      type="button"
                      onClick={handleViewFullPaymentHistory}
                      className="shrink-0 text-sm font-medium text-[#4369a0] hover:underline"
                    >
                      Payment history
                    </button>
                  </div>
                  <div className="p-5">
                    {memberPayments.length > 0 ? (
                      <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                        {memberPayments.slice(0, 5).map((payment) => (
                          <div
                            key={payment.id}
                            className="flex items-center justify-between gap-4 bg-white px-4 py-3 first:rounded-t-lg last:rounded-b-lg"
                          >
                            <div>
                              <p className="font-semibold text-gray-900">
                                ₹{payment.amount}
                              </p>
                              <p className="text-sm text-gray-500">
                                {payment.month} {payment.year}
                              </p>
                            </div>
                            <div className="text-right">
                              <StatusBadge status={payment.paymentType.toLowerCase() as Status} />
                              <p className="mt-1 text-xs text-gray-500">
                                {formatDate(payment.paymentDate)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 py-10 text-center">
                        <CreditCard className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                        <p className="text-sm text-gray-500">
                          No payments recorded yet
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      <DocumentViewer
        isOpen={!!viewingDocument}
        onClose={handleCloseDocument}
        documentName={viewingDocument?.name ?? ""}
        documentUrl={viewingDocument?.url}
        title={viewingDocument?.title ?? "Document"}
      />

      <ViewPayment
        member={memberDetails}
        open={isPaymentViewOpen}
        onOpenChange={setIsPaymentViewOpen}
      />
    </>
  );
}
